import React, { useContext, useState } from "react";
import { Modal } from "react-bootstrap";
import { CommonContext } from "hooks/CommonContext";
import apiInterceptor from "services/apiInterceptor";
const { REACT_APP_CLOUD_FUNCTIONS_URL } = process.env;

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

  /* ── Dark obsidian modal ── */
  .lib-modal .modal-content {
    background: #0f1629 !important;
    border: 1px solid #1e2d4a !important;
    border-radius: 16px !important;
    box-shadow: 0 24px 64px rgba(0,0,0,0.6) !important;
    font-family: 'Inter', system-ui, sans-serif !important;
  }

  .lib-modal .modal-body {
    padding: 28px 28px 24px !important;
  }

  .lib-modal-title {
    font-size: 17px;
    font-weight: 700;
    color: #f0f4ff;
    margin: 0 0 10px;
  }

  .lib-modal-body {
    font-size: 14px;
    color: #8b9dc3;
    line-height: 1.6;
    margin: 0;
  }

  .lib-modal-body strong {
    color: #dbe4f8;
    font-weight: 600;
  }

  .lib-modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 24px;
  }

  /* Secondary "No, just leave" */
  .lib-btn-secondary {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 9px 20px;
    background: #162035;
    color: #8b9dc3;
    border: 1px solid #1e2d4a;
    border-radius: 9px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13.5px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
    white-space: nowrap;
  }

  .lib-btn-secondary:hover:not(:disabled) {
    background: #1e2d4a;
    color: #c4d0ea;
  }

  .lib-btn-secondary:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  /* Primary "Yes, mark completed" — cyan */
  .lib-btn-primary {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 9px 20px;
    background: linear-gradient(135deg, #06b6d4 0%, #22d3ee 100%);
    color: #04111a;
    border: none;
    border-radius: 9px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13.5px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 3px 14px rgba(34,211,238,0.25);
    transition: filter 0.15s, transform 0.12s;
    white-space: nowrap;
  }

  .lib-btn-primary:hover:not(:disabled) {
    filter: brightness(1.08);
    transform: translateY(-1px);
  }

  .lib-btn-primary:disabled {
    background: #1e2d4a;
    color: #526080;
    box-shadow: none;
    cursor: not-allowed;
    filter: none;
    transform: none;
  }
`;

let _inj = false;
const inject = () => {
  if (_inj) return;
  const s = document.createElement("style");
  s.textContent = STYLES;
  document.head.appendChild(s);
  _inj = true;
};

const LeaveIconButton = () => {
  inject();
  const { userOwnData, socketRef, room_id, userName, setIsInterviewEnded } =
    useContext(CommonContext);

  const [showConfirm, setShowConfirm] = useState(false);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);

  const isCandidate = userOwnData?.isCandidate;

  const callSetInterviewStatus = async (status = "C") => {
    try {
      setIsStatusUpdating(true);
      await apiInterceptor.post(
        REACT_APP_CLOUD_FUNCTIONS_URL + "/interview_status_update",
        {
          interview_guid: room_id,
          candidate_application_guid: userOwnData?.candidateApplicationGuid,
          status,
        }
      );
    } catch (err) {
      console.error("Error updating interview status:", err);
    } finally {
      setIsStatusUpdating(false);
    }
  };

  const leaveInterview = async (markCompleted = false) => {
    socketRef.current.emit("leave", {
      username: userName,
      userId: userOwnData.userId,
      room_id,
      guid: userOwnData.guid,
    });
    if (!isCandidate && markCompleted) callSetInterviewStatus();
    setIsInterviewEnded(true);
  };

  const handleButtonClick = () => {
    if (isCandidate) leaveInterview(false);
    else setShowConfirm(true);
  };

  return (
    <>
      {/* Leave / End button — uses the shared ftr-leave class from footer index */}
      <button className="ftr-leave" onClick={handleButtonClick}>
        {isCandidate ? "Leave" : "End"}
        <span className="d-none d-md-inline"> Interview</span>
      </button>

      {/* Confirmation modal — dark obsidian */}
      <Modal
        centered
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        size="md"
        dialogClassName="lib-modal"
      >
        <Modal.Body>
          <h5 className="lib-modal-title">End Interview</h5>
          <p className="lib-modal-body">
            Do you want to mark this interview as <strong>Completed</strong>?
          </p>
          <div className="lib-modal-actions">
            <button
              className="lib-btn-secondary"
              disabled={isStatusUpdating}
              onClick={() => { setShowConfirm(false); leaveInterview(false); }}
            >
              No, just leave
            </button>
            <button
              className="lib-btn-primary"
              disabled={isStatusUpdating}
              onClick={() => { setShowConfirm(false); leaveInterview(true); }}
            >
              {isStatusUpdating ? "Updating…" : "Yes, Mark Completed"}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default LeaveIconButton;